/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2024 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import Notify from "@eisinc/App/Components/Notify";
import MakeAPaymentBox from "@eisinc/App/Components/PaymentCenter/MakeAPaymentBox";
import SelectInvoices from "@eisinc/App/Components/PaymentCenter/SelectInvoices";
import loadInvoicePaymentItems from "@eisinc/App/Handlers/LoadInvoicePaymentItems";
import getSystemList from "@eisinc/App/Handlers/XNG/getSystemList";
import { GetSystemListValuesModel } from "@eisinc/App/Services/SystemListService";
import { xHeaderState } from "@eisinc/App/Store/Reducers";
import OpenItemsTable from "@eisinc/App/Widgets/PaymentCenter/OpenItemsTable";
import { InvoicePaymentModel } from "@eisinc/XDL/interfaces";
import { color } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

export interface PaymentCenterStyle {
    wrapper?: InjectableCss;
    leftSection?: GridItemProps;
    rightSection?: GridItemProps;
    subCopy?: TypographyProps;
}

export const styles: PaymentCenterStyle = {
    wrapper: {
        css: css``,
    },
    leftSection: {
        width: [12, 12, 12, 9, 9],
        css: css`
            flex-direction: column;
        `,
    },
    rightSection: {
        width: [12, 12, 12, 3, 3],
    },
    subCopy: {
        lineHeight: "24px",
    },
};

const mapStateToProps = (state: ApplicationState & xHeaderState) => {
    return {
        sessionCurrencyCode: state.context.session.currency?.currencyCode,
        selectedBillToId: state.context.session.billToId,
        salesOrg: getBillToState(state, state.context.session.billToId)?.value?.properties?.salesOrg,
        bTCurrencyCode: getBillToState(state, state.context.session.billToId)?.value?.properties?.currencyCode,
    };
};

const mapDispatchToProps = {
    loadInvoicePaymentItems,
    getSystemList,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const Separator = styled.div`
    border-bottom: 1px solid ${color("common.ui4")};
    width: 100%;
    margin: 15px 0;
`;

const PaymentCenterWidget: React.FunctionComponent<Props> = ({
    id,
    loadInvoicePaymentItems,
    getSystemList,
    salesOrg,
    bTCurrencyCode,
    sessionCurrencyCode,
}: Props) => {
    const toasterContext = React.useContext(ToasterContext);

    const [isOpenItemsStep, setIsOpenItemsStep] = React.useState(true);
    const [invoicePayments, setInvoicePayments] = React.useState<InvoicePaymentModel[] | undefined>();
    const [isLoadingInvoices, setIsLoadingInvoices] = React.useState(true);
    const [allowedCurrencies, setAllowedCurrencies] = React.useState<GetSystemListValuesModel[]>();
    const [isReady, setIsReady] = React.useState(false);

    const renderPageTitle = () => (
        <Typography
            css={css`
                @media print {
                    display: none;
                }
            `}
            variant="h1"
            as="h1"
            data-test-selector="PageTitle"
        >
            {isOpenItemsStep
                ? siteMessage("Custom_Payment_Center_OpenItems_PageTitle")
                : siteMessage("Custom_Payment_Center_PageTitle")}
        </Typography>
    );

    const loadData = () => {
        loadInvoicePaymentItems({
            onComplete: (response: any) => {
                const apiResult = response.apiResult;
                if (apiResult) {
                    setInvoicePayments(apiResult.arTransactionsList);
                }

                setIsLoadingInvoices(false);
            },
        });
    };

    const isAllowedToPaymentCenter = React.useMemo(() => {
        const currencyCode = bTCurrencyCode || sessionCurrencyCode;
        const role = `${salesOrg}_${currencyCode}`;

        return allowedCurrencies?.some(currency => [role].includes(currency.name));
    }, [salesOrg, bTCurrencyCode, allowedCurrencies, sessionCurrencyCode]);

    const loadAllowedCurrencies = () => {
        getSystemList({
            systemListName: "Custom_AllowedInvoicePaymentCustomers",
            onError: (errorMessage: string) => {
                toasterContext.addToast({ body: errorMessage, messageType: "danger" });
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.successful) {
                    setAllowedCurrencies(resultProps.apiResult.result);
                }
            },
        });
    };

    React.useEffect(() => {
        loadAllowedCurrencies();
    }, []);

    React.useEffect(() => {
        if (isOpenItemsStep) {
            loadData();
        }
    }, [isOpenItemsStep]);

    React.useEffect(() => {
        if (salesOrg && (bTCurrencyCode || sessionCurrencyCode) && allowedCurrencies) {
            setIsReady(true);
        }
    }, [salesOrg, bTCurrencyCode, allowedCurrencies, sessionCurrencyCode]);

    if (!isReady) {
        return null;
    }

    if (!isAllowedToPaymentCenter && !isLoadingInvoices) {
        return (
            <Notify
                messageType="danger"
                body={<Typography>{siteMessage("Custom_Payment_Center_OpenItems_Not_Allowed_Users")}</Typography>}
            />
        );
    }

    return (
        <StyledWrapper className="payment-center-wrapper" {...styles.wrapper}>
            {isOpenItemsStep ? (
                <StyledWrapper>
                    <GridContainer gap={20}>
                        <GridItem {...styles.leftSection}>
                            {renderPageTitle()}
                            <Typography variant="p" {...styles.subCopy}>
                                {siteMessage("Custom_Payment_Center_OpenItems_Subcopy")}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.rightSection}>
                            <MakeAPaymentBox
                                setIsOpenItemsStep={setIsOpenItemsStep}
                                isLoadingInvoices={isLoadingInvoices}
                            />
                        </GridItem>
                    </GridContainer>
                    <Separator />
                    <OpenItemsTable invoicePaymentItems={invoicePayments} isLoadingInvoices={isLoadingInvoices} />
                </StyledWrapper>
            ) : (
                <StyledWrapper>
                    {renderPageTitle()}
                    <SelectInvoices invoicePaymentItems={invoicePayments} setIsOpenItemsStep={setIsOpenItemsStep} />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PaymentCenterWidget),
    definition: {
        group: "Xngage Custom" as any,
        displayName: "Payment Center",
    },
};

export default widgetModule;
